import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../../../lib/api";
import { queryKeys } from "../../../lib/queryKeys";
import { Popover } from "../../ui/Popover";
import { cn } from "../../ui/cn";
import {
  RANGE_PRESETS,
  type RangePreset,
  type RangeValue,
} from "../RangeFilter";
import { CHART_LABELS, type ChartType } from "./chart-types";

/**
 * Everything that narrows one card, behind one button.
 *
 * A card used to wear its controls in a row: a date select, a chart select,
 * and on two cards a third select of its own. Four cards deep that is a
 * toolbar wider than the heading it sits beside, and on a phone it wrapped
 * onto two rows before the card had drawn anything. The controls are not the
 * card; the figure is.
 *
 * So they collapse into one button that says what is currently applied, and
 * open into a panel that discloses progressively: the top level is a list of
 * categories, and choosing one replaces the list with that category's options
 * *in the same panel*. One surface, one back step, never a menu that spawns
 * another menu -- a chain of popovers is a thing people lose, and on a phone
 * the second one is usually off the edge.
 *
 * ## Why the label is what it is
 *
 * "Filters (3)" when anything non-default is set, and the date range's own
 * label otherwise. The date range is the control people change most and the
 * one they need to read without opening anything -- a button that always said
 * "Filters" would make "which month am I looking at" a click away on every
 * card at once.
 *
 * ## What is not in here
 *
 * The colour picker. It is not a filter: it changes how the same figures are
 * painted, and burying it under a button labelled with a date range would be
 * filing it where nobody would look for it.
 */

const RANGE_LABELS: Record<RangePreset, string> = {
  today: "Today",
  "this-week": "This week",
  "last-week": "Last week",
  "this-month": "This month",
  "last-month": "Last month",
  "3-months": "3 months",
  "12-months": "12 months",
  rolling: "Rolling 30 days",
  custom: "Custom…",
};

/** The Agent list's entry for tickets nobody holds. Mirrors the server's. */
export const UNASSIGNED_AGENT = 0;

export interface CardFilterValue {
  range: RangeValue;
  chart: ChartType;
  pipelineIds: number[];
  agentIds: number[];
}

type Category = "range" | "chart" | "pipeline" | "agent";

export function CardFilters({
  value,
  charts,
  onChange,
  /** Agents are only offered to managers; the server enforces it regardless. */
  offerAgents,
  title,
}: {
  value: CardFilterValue;
  charts: readonly ChartType[];
  onChange: (next: Partial<CardFilterValue>) => void;
  offerAgents: boolean;
  title: string;
}) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<Category | null>(null);

  /*
   * Only the categories this card can answer. A card that draws one shape has
   * no chart choice, and one that is a figure about right now has no range --
   * the same rule the frame already applies to the controls it replaces.
   */
  const categories: Category[] = [
    "range",
    ...(charts.length > 1 ? (["chart"] as const) : []),
    "pipeline",
    ...(offerAgents ? (["agent"] as const) : []),
  ];

  const active =
    value.pipelineIds.length + value.agentIds.length + (value.chart ? 0 : 0);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        /* Back to the category list each time, so reopening never lands
           somewhere the reader did not leave it. */
        if (!next) setCategory(null);
      }}
      side="bottom"
      align="right"
      overlay
      label={`Filters for ${title}`}
      trigger={
        <span
          className={cn(
            "flex h-7 items-center gap-1 rounded-md border border-line px-2 text-xs",
            "text-content hover:border-brand-300",
            active > 0 && "border-brand-300 text-brand-600 dark:text-brand-400",
          )}
        >
          {active > 0
            ? `Filters (${active})`
            : RANGE_LABELS[value.range.preset]}
          <span aria-hidden className="text-[9px] text-faint">
            ▾
          </span>
        </span>
      }
    >
      <div className="w-60">
        {category === null ? (
          <ul className="py-1">
            {categories.map((entry) => (
              <li key={entry}>
                <button
                  type="button"
                  onClick={() => setCategory(entry)}
                  className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs text-content hover:bg-surface-2"
                >
                  <span className="flex-1">{CATEGORY_LABELS[entry]}</span>
                  <span className="max-w-[7rem] truncate text-[11px] text-faint">
                    {summaryOf(entry, value)}
                  </span>
                  <span aria-hidden className="text-[9px] text-faint">
                    ›
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div>
            <button
              type="button"
              onClick={() => setCategory(null)}
              className="flex w-full items-center gap-1.5 border-b border-line px-2.5 py-1.5 text-left text-xs font-medium text-content hover:bg-surface-2"
            >
              <span aria-hidden className="text-[9px] text-faint">
                ‹
              </span>
              {CATEGORY_LABELS[category]}
            </button>

            {category === "range" ? (
              <RangePanel
                value={value.range}
                onChange={(range) => onChange({ range })}
              />
            ) : category === "chart" ? (
              <ChoiceList
                options={charts.map((type) => ({
                  id: type,
                  label: CHART_LABELS[type],
                }))}
                selected={value.chart}
                onSelect={(chart) => onChange({ chart: chart as ChartType })}
              />
            ) : category === "pipeline" ? (
              <PipelinePanel
                selected={value.pipelineIds}
                onChange={(pipelineIds) => onChange({ pipelineIds })}
              />
            ) : (
              <AgentPanel
                selected={value.agentIds}
                onChange={(agentIds) => onChange({ agentIds })}
              />
            )}
          </div>
        )}
      </div>
    </Popover>
  );
}

const CATEGORY_LABELS: Record<Category, string> = {
  range: "Date range",
  chart: "Chart type",
  pipeline: "Pipeline",
  agent: "Agent",
};

/** What a category row says it is currently set to, at a glance. */
function summaryOf(category: Category, value: CardFilterValue): string {
  if (category === "range") return RANGE_LABELS[value.range.preset];
  if (category === "chart") return CHART_LABELS[value.chart];
  const count =
    category === "pipeline" ? value.pipelineIds.length : value.agentIds.length;
  return count === 0 ? "All" : `${count} selected`;
}

function RangePanel({
  value,
  onChange,
}: {
  value: RangeValue;
  onChange: (value: RangeValue) => void;
}) {
  return (
    <div>
      <ChoiceList
        options={RANGE_PRESETS.map((preset) => ({
          id: preset,
          label: RANGE_LABELS[preset],
        }))}
        selected={value.preset}
        onSelect={(preset) =>
          onChange({ ...value, preset: preset as RangePreset })
        }
      />

      {/* Only once custom is chosen, so the two date fields are not two rows
          of empty boxes above every other preset. */}
      {value.preset === "custom" ? (
        <div className="flex items-center gap-1.5 border-t border-line px-2.5 py-2">
          <input
            type="date"
            value={value.from ?? ""}
            max={value.to}
            onChange={(event) =>
              onChange({ ...value, from: event.target.value })
            }
            aria-label="From"
            className="h-7 min-w-0 flex-1 rounded-md border border-line bg-surface px-1.5 text-xs text-content"
          />
          <span className="text-xs text-faint">to</span>
          <input
            type="date"
            value={value.to ?? ""}
            min={value.from}
            onChange={(event) => onChange({ ...value, to: event.target.value })}
            aria-label="To"
            className="h-7 min-w-0 flex-1 rounded-md border border-line bg-surface px-1.5 text-xs text-content"
          />
        </div>
      ) : null}
    </div>
  );
}

/** A single-select list, for the two categories that are a choice of one. */
function ChoiceList({
  options,
  selected,
  onSelect,
}: {
  options: readonly { id: string; label: string }[];
  selected: string;
  onSelect: (id: string) => void;
}) {
  return (
    <ul className="max-h-64 overflow-y-auto py-1">
      {options.map((option) => (
        <li key={option.id}>
          <button
            type="button"
            onClick={() => onSelect(option.id)}
            aria-pressed={option.id === selected}
            className={cn(
              "flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-surface-2",
              option.id === selected
                ? "text-brand-600 dark:text-brand-400"
                : "text-content",
            )}
          >
            <span aria-hidden className="w-3 text-[10px]">
              {option.id === selected ? "✓" : ""}
            </span>
            {option.label}
          </button>
        </li>
      ))}
    </ul>
  );
}

function PipelinePanel({
  selected,
  onChange,
}: {
  selected: number[];
  onChange: (ids: number[]) => void;
}) {
  /* The configured pipelines, live: a new one is a new row the day it is
     created, exactly as the segment report resolves them. */
  const pipelines = useQuery({
    queryKey: queryKeys.pipelines,
    queryFn: api.pipelines,
    staleTime: 5 * 60_000,
  });

  return (
    <CheckList
      selected={selected}
      onChange={onChange}
      placeholder="Search pipelines"
      options={(pipelines.data ?? [])
        .filter((pipeline) => pipeline.isActive !== false)
        .map((pipeline) => ({ id: pipeline.id, label: pipeline.name }))}
    />
  );
}

function AgentPanel({
  selected,
  onChange,
}: {
  selected: number[];
  onChange: (ids: number[]) => void;
}) {
  const agents = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    staleTime: 5 * 60_000,
  });

  return (
    <CheckList
      selected={selected}
      onChange={onChange}
      placeholder="Search agents"
      options={(agents.data ?? []).map((agent) => ({
        id: agent.id,
        label: agent.name,
      }))}
      /*
       * Pinned below a divider rather than sorted in among the names, because
       * it is not a person and would otherwise sit under U between two
       * colleagues. It is also the entry somebody goes looking for
       * deliberately -- "what has nobody picked up" is a different question
       * from "what is Priya doing".
       */
      fixed={{ id: UNASSIGNED_AGENT, label: "Unassigned" }}
    />
  );
}

/**
 * A searchable multi-select.
 *
 * Search filters the list rather than the selection, so a box with something
 * typed in it never silently drops a choice made a moment earlier -- the
 * count on the button is the truth, and ticked entries hidden by a search are
 * still ticked when it is cleared.
 */
function CheckList({
  options,
  selected,
  onChange,
  placeholder,
  fixed,
}: {
  options: readonly { id: number; label: string }[];
  selected: number[];
  onChange: (ids: number[]) => void;
  placeholder: string;
  fixed?: { id: number; label: string };
}) {
  const [search, setSearch] = useState("");
  const term = search.trim().toLowerCase();
  const shown = term
    ? options.filter((option) => option.label.toLowerCase().includes(term))
    : options;

  const toggle = (id: number) =>
    onChange(
      selected.includes(id)
        ? selected.filter((entry) => entry !== id)
        : [...selected, id],
    );

  return (
    <div>
      <div className="border-b border-line px-2 py-1.5">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          className={cn(
            "h-7 w-full rounded-md border border-line bg-surface px-2 text-xs",
            "text-content placeholder:text-faint",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
          )}
        />
      </div>

      <ul className="max-h-52 overflow-y-auto py-1">
        {shown.length === 0 ? (
          <li className="px-2.5 py-1.5 text-[11px] text-faint">No matches.</li>
        ) : (
          shown.map((option) => (
            <li key={option.id}>
              <Check
                label={option.label}
                checked={selected.includes(option.id)}
                onToggle={() => toggle(option.id)}
              />
            </li>
          ))
        )}
      </ul>

      {fixed ? (
        <div className="border-t border-line py-1">
          <Check
            label={fixed.label}
            checked={selected.includes(fixed.id)}
            onToggle={() => toggle(fixed.id)}
          />
        </div>
      ) : null}

      {selected.length > 0 ? (
        <div className="border-t border-line px-2.5 py-1.5">
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[11px] text-brand-600 hover:underline dark:text-brand-400"
          >
            Clear
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Check({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 px-2.5 py-1 text-xs text-content hover:bg-surface-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="size-3.5 accent-brand-600"
      />
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </label>
  );
}
